// Lesson 2: Aktivitas Siang Hari - Question Configurations

export interface QuestionConfig {
    type: string;
    wordBank: number[]; // Word IDs to use
    questionSentence?: number[]; // Order of word IDs for sentence questions
    correctAnswer?: number[] | number; // Correct answer(s)
    options?: Array<{ id: number; text?: string }>; // Multiple choice options
    imagePrompt?: string; // For image-based questions
}

export interface MatchingPair {
    indonesian: string;
    batak: string;
}

// 1. Vocabulary Introduction Config (Image + Word) - 5 questions
export const unit5Lesson2VocabIntroConfigs = [
    {
        type: "vocab-intro",
        introWordId: 201, // "mangkuling" (Pergi)
        imagePrompt: "person_going_out"
    },
    {
        type: "vocab-intro",
        introWordId: 202, // "mangalusi" (Bekerja)
        imagePrompt: "person_working"
    },
    {
        type: "vocab-intro",
        introWordId: 203, // "manungkun" (Belajar)
        imagePrompt: "person_studying"
    },
    {
        type: "vocab-intro",
        introWordId: 204, // "marmaen" (Bermain)
        imagePrompt: "children_playing"
    },
    {
        type: "vocab-intro",
        introWordId: 205, // "mamangan siang" (Makan siang)
        imagePrompt: "lunch_time"
    }
];

// 2. Vocabulary Quiz (Image → Choose correct word) - 5 questions
export const unit5Lesson2WordTranslationConfigs: QuestionConfig[] = [  
    {
        type: "word-translation",
        wordBank: [202],
        correctAnswer: 202, // "mangalusi"
        options: [
            { id: 202 }, // mangalusi
            { id: 201 }, // mangkuling
            { id: 203 }, // manungkun
            { id: 204 }  // marmaen
        ]
    },
    {
        type: "word-translation",
        wordBank: [204],
        correctAnswer: 204, // "marmaen"
        options: [
            { id: 204 }, // marmaen
            { id: 202 }, // mangalusi
            { id: 205 }, // mamangan siang
            { id: 201 }  // mangkuling
        ]
    },
    {
        type: "word-translation",
        wordBank: [203],
        correctAnswer: 203, // "manungkun"
        options: [
            { id: 203 }, // manungkun
            { id: 204 }, // marmaen
            { id: 202 }, // mangalusi
            { id: 205 }  // mamangan siang
        ]
    },
    {
        type: "word-translation",
        wordBank: [205],
        correctAnswer: 205, // "mamangan siang"
        options: [
            { id: 205 }, // mamangan siang
            { id: 203 }, // manungkun
            { id: 201 }, // mangkuling
            { id: 202 }  // mangalusi
        ]
    },
    {
        type: "word-translation",
        wordBank: [201],
        correctAnswer: 201, // "mangkuling"
        options: [
            { id: 201 }, // mangkuling
            { id: 204 }, // marmaen
            { id: 203 }, // manungkun
            { id: 205 }  // mamangan siang
        ]
    }
];

// 3. Fill in the Blank Questions - 5 questions
export const unit5Lesson2FillInBlankConfigs: QuestionConfig[] = [
    {
        type: "fill-in-the-blank",
        wordBank: [210, 999, 206, 208],
        questionSentence: [210, 999, 206, 208], // "Anggiku _____ di parguruan."
        correctAnswer: 203, // "manungkun"
        options: [
            { id: 203 }, // manungkun
            { id: 202 }, // mangalusi
            { id: 204 }, // marmaen
            { id: 201 }  // mangkuling
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [107, 999, 206, 207],
        questionSentence: [107, 999, 206, 207], // "Ia _____ di pajak."
        correctAnswer: 202, // "mangalusi"
        options: [
            { id: 202 }, // mangalusi
            { id: 203 }, // manungkun
            { id: 205 }, // mamangan siang
            { id: 204 }  // marmaen
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [105, 999, 206, 209],
        questionSentence: [105, 999, 206, 209], // "Au _____ di bagas."
        correctAnswer: 203, // "manungkun"
        options: [
            { id: 203 }, // manungkun
            { id: 201 }, // mangkuling
            { id: 202 }, // mangalusi
            { id: 204 }  // marmaen
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [106, 999, 206, 209],
        questionSentence: [106, 999, 206, 209], // "Ho _____ di bagas."
        correctAnswer: 204, // "marmaen"
        options: [
            { id: 204 }, // marmaen
            { id: 202 }, // mangalusi
            { id: 203 }, // manungkun
            { id: 205 }  // mamangan siang
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [107, 999, 206, 207],
        questionSentence: [107, 999, 206, 207], // "Ia _____ di pajak."
        correctAnswer: 205, // "mamangan siang"
        options: [
            { id: 205 }, // mamangan siang
            { id: 201 }, // mangkuling
            { id: 203 }, // manungkun
            { id: 202 }  // mangalusi
        ]
    }
];

// 4. Compose Sentence Questions - 5 questions
export const unit5Lesson2ComposeSentenceConfigs: QuestionConfig[] = [
    {
        type: "compose-sentences",
        wordBank: [107, 202, 206, 207],
        questionSentence: [107, 202, 206, 207], // "Ia mangalusi di pajak."
        correctAnswer: [107, 202, 206, 207],
        imagePrompt: "person_working_market"
    },
    {
        type: "compose-sentences",
        wordBank: [210, 203, 206, 208],
        questionSentence: [210, 203, 206, 208], // "Anggiku manungkun di parguruan."
        correctAnswer: [210, 203, 206, 208],
        imagePrompt: "sibling_studying_school"
    },
    {
        type: "compose-sentences",
        wordBank: [105, 203, 206, 209],
        questionSentence: [105, 203, 206, 209], // "Au manungkun di bagas."
        correctAnswer: [105, 203, 206, 209],
        imagePrompt: "studying_at_home"
    },
    {
        type: "compose-sentences",
        wordBank: [106, 204, 206, 209],
        questionSentence: [106, 204, 206, 209], // "Ho marmaen di bagas."
        correctAnswer: [106, 204, 206, 209],
        imagePrompt: "playing_at_home"
    },
    {
        type: "compose-sentences",
        wordBank: [107, 205, 206, 207],
        questionSentence: [107, 205, 206, 207], // "Ia mamangan siang di pajak."
        correctAnswer: [107, 205, 206, 207],
        imagePrompt: "lunch_at_market"
    }
];

// 5. Matching Questions - 5 questions
export const unit5Lesson2MatchingConfigs: QuestionConfig[] = [
    {
        type: "matching",
        wordBank: [201, 202, 203],
        correctAnswer: [201, 202, 203],
        options: [
            { id: 201, text: "Pergi" },
            { id: 202, text: "Bekerja" },
            { id: 203, text: "Belajar" }
        ]
    },
    {
        type: "matching",
        wordBank: [204, 205, 206],
        correctAnswer: [204, 205, 206],
        options: [
            { id: 204, text: "Bermain" },
            { id: 205, text: "Makan siang" },
            { id: 206, text: "di" }
        ]
    },
    {
        type: "matching",
        wordBank: [207, 208, 209],
        correctAnswer: [207, 208, 209],
        options: [
            { id: 207, text: "Pasar" },
            { id: 208, text: "Sekolah" },
            { id: 209, text: "Rumah" }
        ]
    },
    {
        type: "matching",
        wordBank: [105, 106, 107],
        correctAnswer: [105, 106, 107],
        options: [
            { id: 105, text: "Saya" },
            { id: 106, text: "Kamu" },
            { id: 107, text: "Dia" }
        ]
    },
    {
        type: "matching",
        wordBank: [202, 203, 210],
        correctAnswer: [202, 203, 210],
        options: [
            { id: 202, text: "Bekerja" },
            { id: 203, text: "Belajar" },
            { id: 210, text: "Saudaraku" }
        ]
    }
];

export const unit5Lesson2WordMatchingPairs: MatchingPair[] = [
    { indonesian: "Pergi", batak: "ᯔᯀᯰᯂᯮᯝᯪᯰ" },
    { indonesian: "Bekerja", batak: "ᯔᯀᯰᯀᯝᯮᯘᯤ" },
    { indonesian: "Belajar", batak: "ᯔᯀᯉᯮᯰᯂᯮᯉ" },
    { indonesian: "Bermain", batak: "ᯔᯀᯒᯔᯀᯧᯉ᯲" },
    { indonesian: "Makan siang", batak: "ᯔᯀᯔᯀᯰᯀᯉ ᯘᯤᯀᯰ" },
    { indonesian: "Pasar", batak: "ᯇᯀᯐᯀᯂ᯲" },
    { indonesian: "Sekolah", batak: "ᯇᯀᯒᯎᯮᯒᯮᯀᯉ" },
    { indonesian: "Rumah", batak: "ᯅᯀᯎᯀᯘ" },
    { indonesian: "Saudaraku", batak: "ᯀᯰᯎᯤᯂᯮ" }
];

export const unit5Lesson2MatchingWordsConfig = {
    type: "matching-words",
    pairs: unit5Lesson2WordMatchingPairs,
};
